import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import {
  CalendarClock,
  ChevronRight,
  FileText,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Receipt,
  Star,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { formatDateOnly } from '@/lib/dates/date-only';
import { formatRenews } from '@/lib/portal/membershipDisplay';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import {
  usePortalHome,
  type PortalHomeAlert,
  type PortalHomeVisit,
} from '@/hooks/portal/usePortalHome';
import { usePortalBookingAddresses } from '@/hooks/portal/usePortalVisits';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalStatusPill } from '@/components/portal/PortalStatusPill';
import { PortalVisitDateBlock } from '@/components/portal/PortalVisitDateBlock';
import { formatVisitPeriod, portalVisitPill } from '@/lib/portal/visitDisplay';
import { officeContacts } from '@/lib/portal/officeContact';
import { usePortalIdentity } from '@/hooks/portal/usePortalAuth';

export const Route = createFileRoute('/(portal)/portal/')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalHomePage,
});

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/** "in 5 days" / "today" / "5 days ago" — a compact relative phrase from an ISO date.
 *  The target is read as a CALENDAR day from its YYYY-MM-DD parts (never
 *  `new Date(iso)` + local getters, which shifts a UTC-midnight date to the prior
 *  day west of UTC) and compared against the viewer's local calendar day. */
function relativeDay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const today = new Date();
  const days = Math.round(
    (Date.UTC(y, m - 1, d) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
      86_400_000,
  );
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 0) return `in ${days} days`;
  return `${Math.abs(days)} days ago`;
}

function PortalHomePage() {
  const home = usePortalHome();
  const addresses = usePortalBookingAddresses();
  const primaryAddress = addresses.data?.[0];
  const addressLine = primaryAddress
    ? [primaryAddress.street, primaryAddress.city, primaryAddress.state].filter(Boolean).join(', ')
    : null;

  return (
    <PortalShell>
      {home.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : home.isError ? (
        <Card>
          <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
            <p>We couldn&apos;t load your dashboard. Please try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void home.refetch()}
              disabled={home.isFetching}
            >
              {home.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {greeting()}
                {home.data.greetingName ? `, ${home.data.greetingName}` : ''}
              </h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {addressLine
                  ? `Everything for ${primaryAddress?.street ?? 'your home'} in one place.`
                  : 'Everything for your service, all in one place.'}
              </p>
            </div>
            {addressLine ? (
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground">
                <MapPin className="size-3.5 shrink-0" />
                <span className="truncate">{addressLine}</span>
              </span>
            ) : null}
          </header>

          {home.data.needsAttention.length > 0 ? (
            <AttentionFeed alerts={home.data.needsAttention} />
          ) : null}

          {home.data.nextVisit ? (
            <section className="space-y-2.5">
              <h2 className="text-base font-semibold tracking-tight">Your next visit</h2>
              <NextVisitCard visit={home.data.nextVisit} />
            </section>
          ) : null}

          {home.data.equipment.total > 0 ? (
            <section className="space-y-2.5">
              <h2 className="text-base font-semibold tracking-tight">Your equipment</h2>
              <EquipmentStrip equipment={home.data.equipment} />
            </section>
          ) : null}

          {home.data.membership ? <MembershipCard membership={home.data.membership} /> : null}
        </div>
      )}
    </PortalShell>
  );
}

function AttentionFeed({ alerts }: { alerts: PortalHomeAlert[] }) {
  return (
    <section className="space-y-2.5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold tracking-tight">Needs your attention</h2>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-portal-accent-surface px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-portal-accent">
          <span className="size-1.5 rounded-full bg-portal-accent" />
          {alerts.length} open
        </span>
      </div>
      <Card className="overflow-hidden p-0">
        <div className="divide-y divide-border">
          {alerts.map((a) => (
            <AlertRow key={`${a.kind}-${a.id}`} alert={a} />
          ))}
        </div>
      </Card>
    </section>
  );
}

function AlertRow({ alert }: { alert: PortalHomeAlert }) {
  const isEstimate = alert.kind === 'estimate';
  const to = isEstimate ? '/portal/estimates/$id' : '/portal/invoices/$id';
  const heading = isEstimate ? 'Estimate ready for your approval' : 'Invoice due';
  const Icon = isEstimate ? FileText : Receipt;
  const rel = alert.dueAt ? relativeDay(alert.dueAt) : null;
  const secondary = alert.dueAt ? (isEstimate ? `expires ${rel}` : `due ${rel}`) : null;
  return (
    <Link
      to={to}
      params={{ id: alert.id }}
      className={`flex items-center gap-3 border-l-2 px-4 py-3.5 transition-colors hover:bg-muted/50 ${
        isEstimate ? 'border-l-portal-accent' : 'border-l-warning-border'
      }`}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-portal-accent-surface text-portal-accent">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{heading}</p>
        <p className="truncate text-xs text-muted-foreground">
          {alert.title}
          {alert.dueAt ? ` · ${isEstimate ? 'expires' : 'due'} ${formatDateOnly(alert.dueAt)}` : ''}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-semibold">{formatCurrency(alert.amount)}</p>
        {secondary ? <p className="text-[11px] text-muted-foreground">{secondary}</p> : null}
      </div>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function NextVisitCard({ visit }: { visit: PortalHomeVisit }) {
  const identity = usePortalIdentity();
  const contacts = officeContacts(identity.data?.tenant);
  const context: string[] = [];
  if (visit.arrivalWindow) {
    const s = formatDateTimeInTimeZone(visit.arrivalWindow.start, visit.timezone, {
      hour: 'numeric',
      minute: '2-digit',
    });
    const e = formatDateTimeInTimeZone(visit.arrivalWindow.end, visit.timezone, {
      hour: 'numeric',
      minute: '2-digit',
    });
    context.push(`${s} – ${e} arrival window`);
  } else if (visit.startAt) {
    context.push(
      formatDateTimeInTimeZone(visit.startAt, visit.timezone, {
        hour: 'numeric',
        minute: '2-digit',
      }),
    );
  } else if (visit.period) {
    context.push(formatVisitPeriod(visit.period));
  }
  if (visit.covered) context.push('covered by your membership · no charge');

  const techDetails = visit.technician
    ? [
        visit.technician.role,
        visit.technician.credentials[0],
        visit.technician.tenureYears
          ? `${visit.technician.tenureYears} ${visit.technician.tenureYears === 1 ? 'year' : 'years'}`
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex gap-3">
          <PortalVisitDateBlock visit={visit} weekday="long" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium">{visit.serviceName ?? 'Service visit'}</p>
              {(() => {
                const pill = portalVisitPill(visit);
                return <PortalStatusPill label={pill.label} tone={pill.tone} />;
              })()}
            </div>
            {context.length > 0 ? (
              <p className="mt-1 text-xs text-muted-foreground">{context.join(' · ')}</p>
            ) : null}
          </div>
        </div>

        {visit.technicianName ? (
          <div className="flex items-center gap-2.5 rounded-lg border border-border p-2.5">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
              {initialsOf(visit.technicianName)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{visit.technicianName}</p>
              <p className="truncate text-xs text-muted-foreground">
                {techDetails || 'Your technician'}
              </p>
            </div>
            <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-success-surface px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success sm:inline-flex">
              <span className="size-1.5 rounded-full bg-success" />
              Your technician
            </span>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/portal/visits">
              <CalendarClock className="mr-1.5 size-4" />
              Reschedule
            </Link>
          </Button>
          {contacts.map((contact) => {
            const Icon = contact.kind === 'phone' ? Phone : Mail;
            return (
              <Button key={contact.kind} variant="ghost" size="sm" asChild>
                <a href={contact.href}>
                  <Icon className="mr-1.5 size-4" />
                  {contact.kind === 'phone' ? 'Call the office' : 'Email the office'}
                </a>
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function EquipmentStrip({
  equipment,
}: {
  equipment: { total: number; needAttention: number; underWarranty: number };
}) {
  const stats = [
    { label: 'Tracked units', value: String(equipment.total) },
    {
      label: 'Need attention',
      value: String(equipment.needAttention),
      warn: equipment.needAttention > 0,
    },
    { label: 'Under warranty', value: String(equipment.underWarranty) },
  ];
  return (
    <Card>
      <CardContent className="grid grid-cols-3 gap-4 py-4">
        {stats.map((s) => (
          <div key={s.label} className="border-l border-border pl-4 first:border-l-0 first:pl-0">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{s.label}</p>
            <p className={`mt-0.5 text-2xl font-semibold ${s.warn ? 'text-destructive' : ''}`}>
              {s.value}
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function MembershipCard({
  membership,
}: {
  membership: {
    planName: string | null;
    status: string;
    nextBillingDate: string | null;
    isComplimentary?: boolean;
    contractEndDate?: string | null;
  };
}) {
  return (
    <Link to="/portal/memberships" className="block">
      <Card className="transition-colors hover:bg-muted/50">
        <CardContent className="flex items-center gap-3 py-4">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-portal-accent-surface text-portal-accent">
            <Star className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{membership.planName ?? 'Membership'}</p>
            <p className="text-xs text-muted-foreground">
              {membership.status === 'ACTIVE' ? 'Active plan' : membership.status}
              {membership.isComplimentary
                ? membership.contractEndDate
                  ? ` · ends ${formatDateOnly(membership.contractEndDate)}`
                  : ''
                : formatRenews(membership)}
            </p>
          </div>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        </CardContent>
      </Card>
    </Link>
  );
}
